import React from "react";
import { Link } from "react-router-dom";
import { FaStar, FaHeart, FaUsers, FaAward } from "react-icons/fa";
import { useLanguage } from "../context/LanguageContext";

const About = () => {
  const { t } = useLanguage();
  const values = [
    {
      icon: <FaStar className="text-5xl text-marrakech-gold" />,
      titleKey: "about.excellence",
      descriptionKey: "about.excellenceDesc",
    },
    {
      icon: <FaHeart className="text-5xl text-marrakech-gold" />,
      titleKey: "about.passion",
      descriptionKey: "about.passionDesc",
    },
    {
      icon: <FaUsers className="text-5xl text-marrakech-gold" />,
      titleKey: "about.hospitality",
      descriptionKey: "about.hospitalityDesc",
    },
    {
      icon: <FaAward className="text-5xl text-marrakech-gold" />,
      titleKey: "about.innovation",
      descriptionKey: "about.innovationDesc",
    },
  ];

  return (
    <div className="about">
      {/* Hero Section */}
      <section
        className="relative h-96 flex items-center justify-center parallax overlay"
        style={{
          backgroundImage: "url(/images/DSC08362.webp)",
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <h1
            className="text-5xl md:text-6xl font-elegant font-bold mb-4"
            data-aos="fade-up"
          >
            {t("about.title")}
          </h1>
          <p
            className="text-xl md:text-2xl"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            {t("about.uniqueExperience")}
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div data-aos="fade-right">
              <h2 className="text-4xl md:text-5xl font-elegant font-bold text-marrakech-dark mb-6">
                {t("about.ourStory")}
              </h2>
              <p className="text-lg text-black mb-6 leading-relaxed">
                {t("about.storyPart1")}
              </p>
              <p className="text-lg text-black mb-6 leading-relaxed">
                {t("about.storyPart2")}
              </p>
              <p className="text-lg text-black mb-6 leading-relaxed">
                {t("about.storyPart3")}
              </p>
            </div>
            <div data-aos="fade-left">
              <img
                src="/images/DSC08396.webp"
                alt="Jana Marrakech Restaurant"
                className="rounded-lg shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Concept Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl md:text-5xl font-elegant font-bold text-marrakech-dark mb-4">
              {t("about.concept")}
            </h2>
            <p className="text-lg text-marrakech-clay max-w-3xl mx-auto">
              {t("about.conceptDesc")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div
              data-aos="fade-up"
              className="bg-[#eee1ca] p-8 rounded-lg shadow-lg"
            >
              <h3 className="text-2xl font-bold text-[#72201e] mb-4 text-center">
                {t("about.moroccanCuisine")}
              </h3>
              <p className="text-black leading-relaxed">
                {t("about.moroccanCuisineDesc")}
              </p>
            </div>

            <div
              data-aos="fade-up"
              data-aos-delay="100"
              className="bg-[#eee1ca] p-8 rounded-lg shadow-lg"
            >
              <h3 className="text-2xl font-bold text-[#72201e] mb-4 text-center">
                {t("about.showAmbiance")}
              </h3>
              <p className="text-black leading-relaxed">
                {t("about.showAmbianceDesc")}
              </p>
            </div>

            {/* last item centered */}
            <div
              data-aos="fade-up"
              data-aos-delay="200"
              className="bg-[#eee1ca] p-8 rounded-lg shadow-lg md:col-span-2 md:w-1/2 md:mx-auto"
            >
              <h3 className="text-2xl font-bold text-[#72201e] mb-4 text-center">
                {t("about.exceptionalSetting")}
              </h3>
              <p className="text-black leading-relaxed">
                {t("about.exceptionalSettingDesc")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl md:text-5xl font-elegant font-bold text-marrakech-dark mb-4">
              {t("about.ourValues")}
            </h2>
            <p className="text-lg text-marrakech-clay max-w-2xl mx-auto">
              {t("about.ourValuesDesc")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                data-aos="fade-up"
                data-aos-delay={index * 100}
                className="text-center p-6 card-hover bg-[#eee1ca] rounded-lg"
              >
                {/* <div className="flex justify-center mb-4">{value.icon}</div> */}
                <h3 className="text-2xl font-bold text-[#72201e] mb-4">
                  {t(value.titleKey)}
                </h3>
                <p className="text-black">{t(value.descriptionKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team/Experience Section */}
      <section
        className="py-20 parallax overlay"
        style={{
          backgroundImage: "url(/images/DSC08460.webp)",
        }}
      >
        <div className="container mx-auto px-4 text-center text-white">
          <div data-aos="fade-up">
            <h2 className="text-4xl md:text-5xl font-elegant font-bold mb-6">
              {t("about.dedicatedTeam")}
            </h2>
            <p className="text-xl max-w-3xl mx-auto mb-8 leading-relaxed">
              {t("about.dedicatedTeamDesc")}
            </p>
            <p className="text-xl max-w-3xl mx-auto leading-relaxed">
              {t("about.tradition")}
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white text-balck">
        <div className="container mx-auto px-4 text-center" data-aos="fade-up">
          <h2 className="text-4xl md:text-5xl font-elegant font-bold mb-6">
            {t("about.joinUs")}
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            {t("about.joinUsDesc")}
          </p>
          <a
            href="/reservation"
            className="btn-primary bg-white text-marrakech-terracotta hover:bg-primary hover:text-white border-white"
          >
            {t("about.reserveNow")}
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;
